import React from 'react';
import styled, { keyframes } from 'styled-components';
import {
  BACKFILL_TERMINAL_STATUSES,
  cancelScoreBackfill,
  fetchScoreBackfill,
  openScoreBackfillStream,
} from '@services/scoreBackfills';
import { createTimeInstance } from '@utils/time';
import { Body5, Body7, ConfirmModal, Container } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import { StatusBadge } from './StatusBadge';
import { SnapshotOutput } from './SnapshotOutput';
import {
  DEFAULT_MARKET_CAP_VERSION,
  deriveDisplayStatus,
  formatCount,
  formatMarketCapVersion,
  marketCapVersionOf,
  formatScored,
  formatUsd,
  formatSpecId,
  formatScope,
  formatJobWindow,
  isLegacyWindow,
  isStampedAtScoringTime,
  LEGACY_WINDOW_NOTE,
  STAMPED_AT_SCORING_TIME_LABEL,
  STAMPED_AT_SCORING_TIME_NOTE,
  formatUniverseKind,
  formatIndex,
} from './format';

const POLL_DELAY = 5000;

const shimmer = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(320%); }
`;

const ProgressBarWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 16px;
  background-color: ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: ${({ theme }) => theme.themeColors.primaryCtaButton};
  border-radius: 3px;
  transition: width 0.3s ease-in-out;
  width: ${({ $percent }) => $percent}%;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ProgressBarShimmer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 40%;
  border-radius: 3px;
  background: linear-gradient(90deg, transparent, ${({ theme }) => theme.themeColors.primaryCtaButton}, transparent);
  animation: ${shimmer} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    left: 0;
    width: 100%;
    opacity: 0.5;
  }
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ theme, $tone }) => theme.themeColors[$tone]};
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.themeColors.error};

  .base-text {
    color: ${({ theme }) => theme.themeColors.error};
  }
`;

const SummaryBox = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  padding: 16px;
  margin-top: 16px;
`;

const ErrorBox = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.negative};
  border-radius: 6px;
  padding: 16px;
  margin-top: 16px;
`;

const isTerminal = (job) => !!job && BACKFILL_TERMINAL_STATUSES.includes(job.status);

const computePct = (job) => {
  if (!job) return null;
  if (job.pct !== null && job.pct !== undefined) {
    return Math.min(100, Math.max(0, Math.round(job.pct)));
  }
  if (job.total && job.completed != null) {
    return Math.min(100, Math.round((job.completed / job.total) * 100));
  }
  return null;
};

// One-line recap of the run params, echoing only fields present on the job.
const summarizeUniverse = (universe) => {
  if (!universe || !universe.kind) return null;
  const kind = formatUniverseKind(universe.kind);
  if (universe.kind === 'index' && universe.value) return `${kind}: ${formatIndex(universe.value)}`;
  if (universe.kind === 'tickers') {
    const count = Array.isArray(universe.value) ? universe.value.length : null;
    return count != null ? `${kind} (${formatCount(count)})` : kind;
  }
  return kind;
};

const buildRecap = (job) => {
  if (!job) return null;
  const parts = [];
  if (job.score_spec_id) parts.push(formatSpecId(job.score_spec_id));
  const universe = summarizeUniverse(job.universe);
  if (universe) parts.push(universe);
  if (job.occasion_from || job.occasion_to) parts.push(formatJobWindow(job));
  if (job.scope) parts.push(formatScope(job.scope));
  // Like the band below: mark the exception, stay silent on the norm. The window part above already
  // ends on the request date for such a job; this says why there was no end date to choose.
  if (isStampedAtScoringTime(job)) parts.push(STAMPED_AT_SCORING_TIME_LABEL);
  // Only a non-DEFAULT version earns a part, mirroring isLegacyWindow: mark the exception, stay
  // silent on the norm. Unlabelled therefore means v1, unambiguously and forever. Derived from the
  // job alone and NOT from universe_options — this route renders outside the page's specs-loading
  // gate, so a deep link to /jobs/:id would otherwise render the recap without the band and pop it
  // in when an unrelated discovery call resolved.
  const marketCapVersion = marketCapVersionOf(job);
  if (marketCapVersion !== DEFAULT_MARKET_CAP_VERSION) parts.push(formatMarketCapVersion(marketCapVersion));
  return parts.length ? parts.join(' · ') : null;
};

export const BackfillProgress = ({ jobId, initialJob, onBack, onViewHistory, onStartSimilar }) => {
  const [job, setJob] = React.useState(initialJob || null);
  const [streamInterrupted, setStreamInterrupted] = React.useState(false);
  const [nowTick, setNowTick] = React.useState(0);
  const [confirmingCancel, setConfirmingCancel] = React.useState(false);
  const [cancelBusy, setCancelBusy] = React.useState(false);
  const [cancelRequested, setCancelRequested] = React.useState(false);
  const [cancelError, setCancelError] = React.useState(null);
  const jobRef = React.useRef(initialJob || null);
  const mountStartRef = React.useRef(createTimeInstance());

  React.useEffect(() => {
    if (!jobId) return undefined;

    const state = { isCanceled: false, pollTimer: null, stream: null, polling: false };

    const applyJob = (next) => {
      if (state.isCanceled || !next) return;
      jobRef.current = next;
      setJob(next);
    };

    const poll = async () => {
      if (state.isCanceled || !state.polling) return;
      const { job: latest } = await fetchScoreBackfill(jobId);
      // The stream may have recovered (onOpen clears state.polling) while this fetch was in
      // flight — bail so a stale snapshot can't overwrite a newer frame or re-arm the timer.
      if (state.isCanceled || !state.polling) return;
      applyJob(latest);
      if (isTerminal(latest)) {
        state.polling = false;
        state.pollTimer = null;
        return;
      }
      state.pollTimer = setTimeout(poll, POLL_DELAY);
    };

    const startPollingFallback = () => {
      if (state.isCanceled || state.pollTimer || isTerminal(jobRef.current)) return;
      state.polling = true;
      state.pollTimer = setTimeout(poll, POLL_DELAY);
    };

    // Self-hydrate on deep-link / refresh when the parent had no job to hand us.
    if (!jobRef.current) {
      (async () => {
        const { job: fetched } = await fetchScoreBackfill(jobId);
        // Don't clobber a stream frame that landed while this hydrate fetch was in flight.
        if (!state.isCanceled && !jobRef.current) applyJob(fetched);
      })();
    }

    state.stream = openScoreBackfillStream(jobId, {
      onOpen: () => {
        if (state.isCanceled) return;
        setStreamInterrupted(false);
        // Stream is healthy again — stop the fallback poll so a stale poll response can't
        // overwrite a newer stream frame (and to end the redundant polling). Clearing `polling`
        // also makes an already-in-flight poll() bail instead of re-arming the timer.
        state.polling = false;
        if (state.pollTimer) {
          clearTimeout(state.pollTimer);
          state.pollTimer = null;
        }
      },
      onProgress: applyJob,
      onComplete: applyJob,
      onError: () => {
        if (state.isCanceled) return;
        // Transport blip or server error: keep the UI live by polling until terminal.
        if (!isTerminal(jobRef.current)) {
          setStreamInterrupted(true);
          startPollingFallback();
        }
      },
    });

    return () => {
      state.isCanceled = true;
      if (state.pollTimer) clearTimeout(state.pollTimer);
      state.stream?.close?.();
    };
  }, [jobId]);

  // `status` is the wire status (gates the terminal panels); `displayStatus` folds in the
  // cancel-requested window, where the job is still `running` but reads as "Canceling…".
  const status = job?.status || 'queued';
  const terminal = isTerminal(job);
  // Trust the local flag as well as the server's cancel_requested_at: the POST returns before
  // the next stream frame lands, and the analyst needs the button to react to their own click.
  // Both are ignored once the job is terminal, which is what clears the optimistic flag.
  const canceling = !terminal && (deriveDisplayStatus(job) === 'canceling' || cancelRequested);
  const displayStatus = canceling ? 'canceling' : status;
  // `job` guards the deep-link hydration window, where status defaults to queued and we'd
  // otherwise offer to cancel a run that may already be finished.
  const cancelable = !!job && !terminal && (status === 'running' || status === 'queued');
  const pct = computePct(job);
  const completed = job?.completed;
  const total = job?.total;
  const indeterminate = !terminal && pct == null;
  const determinate = pct != null;

  // A failed cancel describes the run as it was at that moment. Drop it once the status moves on,
  // so a run that finished on its own can't render "the cancel wasn't recorded" beside its own
  // completion summary.
  React.useEffect(() => {
    setCancelError(null);
  }, [status]);

  // Elapsed / ETA. Advance a tick every 30s while non-terminal so elapsed keeps moving.
  React.useEffect(() => {
    if (terminal) return undefined;
    const id = setInterval(() => setNowTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, [terminal]);

  const start = job?.created_at ? createTimeInstance(job.created_at) : mountStartRef.current;
  const now = createTimeInstance();
  const elapsedMin = Math.max(0, now.diff(start, 'minute'));
  const finishedMin =
    terminal && job?.updated_at ? Math.max(0, createTimeInstance(job.updated_at).diff(start, 'minute')) : elapsedMin;
  // nowTick is read only to force a re-render on the interval above.
  void nowTick;

  const statusDescription = () => {
    if (displayStatus === 'canceling') return 'Canceling — finishing the batch already running';
    if (displayStatus === 'canceled') return 'Canceled';
    if (status === 'queued') return 'Queued — waiting for an open slot';
    if (status === 'running') return pct == null ? 'Starting…' : 'Scoring…';
    if (status === 'completed') return `Finished in ${finishedMin}m`;
    if (status === 'failed') return 'Didn’t finish';
    return 'Starting…';
  };

  const shortLabel = () => {
    if (displayStatus === 'canceling') return 'Canceling';
    if (displayStatus === 'canceled') return 'Canceled';
    if (status === 'queued') return 'Queued';
    if (status === 'completed') return 'Completed';
    if (status === 'failed') return 'Failed';
    return pct == null ? 'Starting' : 'Scoring';
  };

  const ariaValueText = determinate ? `${shortLabel()}, ${pct}%` : statusDescription();

  const renderElapsedLine = () => {
    // Gated on `canceling`, not the wire status: a cancel-requested job is still `running`, and
    // an ETA of "~28m left" next to "stopping at the next batch boundary" contradicts itself.
    if (status !== 'running' || canceling) return null;
    if (pct != null && pct >= 5) {
      const etaMin = Math.round((elapsedMin / pct) * (100 - pct));
      return (
        <Body7 isLowContrast>
          Running {elapsedMin}m · ~{etaMin}m left
        </Body7>
      );
    }
    return <Body7 isLowContrast>Running {elapsedMin}m</Body7>;
  };

  const recap = buildRecap(job);

  // The 200 only means the request was recorded — the worker still has to reach a batch
  // boundary. Flag locally and let the terminal `canceled` frame arrive over the stream.
  const handleConfirmCancel = async () => {
    setCancelBusy(true);
    setCancelError(null);
    const { status: httpStatus, error } = await cancelScoreBackfill(jobId);
    setCancelBusy(false);
    setConfirmingCancel(false);
    if (httpStatus === 200) {
      setCancelRequested(true);
    } else {
      setCancelError(error);
    }
  };

  // Stays enabled while canceling. The endpoint is idempotent, and a worker that dies before
  // reaching a batch boundary would otherwise leave the analyst staring at an inert "Canceling…"
  // button while the run keeps spending — re-requesting is the only recovery, and a reload
  // doesn't restore it because the server now reports cancel_requested_at too.
  const cancelAction = cancelable && (
    <Container left={12}>
      <SkeletonButton onClick={() => setConfirmingCancel(true)} disabled={cancelBusy}>
        Cancel run
      </SkeletonButton>
    </Container>
  );

  const startSimilarAction = onStartSimilar && (
    <Container top={12}>
      <SkeletonButton onClick={() => onStartSimilar(job)}>Start a similar run</SkeletonButton>
    </Container>
  );

  return (
    <div>
      <Container bottom={4}>
        <Body5>
          <Body5 isLowContrast>Score Backfill / </Body5>
          <Body5 bold>Backfill #{jobId}</Body5>
        </Body5>
      </Container>
      {recap && (
        <Container bottom={16}>
          <Body7 isLowContrast>{recap}</Body7>
          {/* The recap echoes the dates as stored; on a job from before the knowable-at switch
              they bounded a different column, and "Start a similar run" below carries them over. */}
          {isLegacyWindow(job) && (
            <Container top={4}>
              <Body7 isLowContrast>{LEGACY_WINDOW_NOTE}</Body7>
            </Container>
          )}
          {/* A stamped run's scores carry the time they were scored, not the occasion's, so a feed
              publish over a window that starts before this run would expect occasions it already
              covered. Said once, where the window is described. */}
          {isStampedAtScoringTime(job) && (
            <Container top={4}>
              <Body7 isLowContrast>{STAMPED_AT_SCORING_TIME_NOTE}</Body7>
            </Container>
          )}
        </Container>
      )}

      <Container bottom={8} row spaceBetween verticallyCenter>
        {/* The live region wraps only the status line. The cancel button sits outside it: a
            control's own label shouldn't be announced by the region as well as by focus, and its
            removal at terminal isn't covered by the default aria-relevant anyway. */}
        <div aria-live="polite">
          <Container row verticallyCenter>
            <StatusBadge status={displayStatus} />
            <Container left={8}>
              <Body5 bold={terminal}>{statusDescription()}</Body5>
            </Container>
            {!terminal && (
              <Container left={8}>
                {streamInterrupted ? (
                  <Pill>
                    <Dot $tone="warning" />
                    <Body7>Reconnecting — run continues, checking every 5s</Body7>
                  </Pill>
                ) : (
                  <Pill>
                    <Dot $tone="positive" />
                    <Body7>Live</Body7>
                  </Pill>
                )}
              </Container>
            )}
          </Container>
        </div>
        <Container row verticallyCenter>
          <Body5 isLowContrast>{determinate ? `${pct}%` : ''}</Body5>
          {cancelAction}
        </Container>
      </Container>

      <div>
        <ProgressBarWrapper
          role="progressbar"
          aria-label="Backfill progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuetext={ariaValueText}
          {...(determinate ? { 'aria-valuenow': pct } : {})}
        >
          {indeterminate ? <ProgressBarShimmer /> : <ProgressBarFill $percent={pct == null ? 0 : pct} />}
        </ProgressBarWrapper>

        {total != null && (
          <Container top={8}>
            <Body7 isLowContrast>{formatScored(completed, total)} scored</Body7>
          </Container>
        )}
        <Container top={4}>{renderElapsedLine()}</Container>
      </div>

      {cancelError && (
        <Container top={8}>
          <ErrorText role="alert">
            <Body7>{cancelError}</Body7>
          </ErrorText>
        </Container>
      )}

      {!terminal && !canceling && (
        <Container top={12}>
          <Body7 isLowContrast>
            Runs take ~30–40 minutes and continue if you leave this page — you can find them again under History.
          </Body7>
        </Container>
      )}

      {canceling && (
        <Container top={12}>
          <Body7 isLowContrast>
            Scoring stops once the batch already running finishes — usually within a few seconds. The scores produced so
            far are then invalidated and no output is written.
          </Body7>
        </Container>
      )}

      {status === 'completed' && (
        <SummaryBox>
          <Body5 bold>Backfill complete</Body5>
          <Container top={8}>
            <Body5>{formatScored(completed, total)} occasions scored.</Body5>
          </Container>
          {completed != null && total != null && completed < total && (
            <Container top={4}>
              <Body7 isLowContrast>
                {formatCount(completed)} scored, {formatCount(total - completed)} skipped
              </Body7>
            </Container>
          )}
          {/* The worker's finish note rides `error` on a completed run too — the shortfall, the
              superseded count of a run stamped at scoring time, an artifact warning — pipe-joined and
              not split by the server. It was shown only on a failed run, which hid the one line that
              explains why a stamped run finished under 100%. Low contrast, not error-coloured: the run
              succeeded. */}
          {job?.error && (
            <Container top={4}>
              <Body7 isLowContrast>{job.error}</Body7>
            </Container>
          )}
          {job?.cost_estimate_usd != null && (
            <Container top={4}>
              <Body7 isLowContrast>Estimated cost: {formatUsd(job.cost_estimate_usd)}</Body7>
            </Container>
          )}
          <SnapshotOutput jobId={jobId} s3Prefix={job?.s3_prefix} />
          {startSimilarAction}
        </SummaryBox>
      )}

      {status === 'canceled' && (
        <SummaryBox>
          <Body5 bold>Backfill canceled</Body5>
          <Container top={8}>
            <Body5>
              {completed != null && total != null
                ? `Stopped after scoring ${formatCount(completed)} of ${formatCount(
                    total
                  )} occasions — all of them were discarded.`
                : 'This run was stopped before it finished.'}
            </Body5>
          </Container>
          <Container top={4}>
            <Body7>
              No output was written, so nothing from this run reaches downstream. Start a new run to score this window
              again.
            </Body7>
          </Container>
          {startSimilarAction}
        </SummaryBox>
      )}

      {status === 'failed' && (
        <ErrorBox role="alert">
          <Body5 bold>Backfill failed</Body5>
          <Container top={8}>
            <Body5>
              {job?.error || 'This backfill didn’t finish and no output was written. Start a new run to try again.'}
            </Body5>
          </Container>
          {startSimilarAction}
        </ErrorBox>
      )}

      {confirmingCancel && (
        <ConfirmModal
          title="Cancel this backfill?"
          body="Scoring stops within about 5 seconds, once the batch already in flight finishes. Everything scored so far is invalidated and no output is written, so this run can’t be resumed — you’d have to start a new one. Spend on batches that already ran isn’t refunded."
          confirmLabel="Cancel run"
          cancelLabel="Keep running"
          maxWidth={500}
          busy={cancelBusy}
          onConfirm={handleConfirmCancel}
          onCancel={() => setConfirmingCancel(false)}
        />
      )}
    </div>
  );
};
